import { useEffect } from 'react';
// import { logger } from "../utils/logger";
const EXTENSION_SAFE_ATTRS: Record<string, string> = {
  'data-ms-editor': 'false',
  'data-gramm': 'false',
  'data-enable-grammarly': 'false',
  'autocomplete': 'off',
  'autocorrect': 'off',
  'autocapitalize': 'off',
  'spellcheck': 'false',
  'data-extension-guard': 'true'
};

const FOCUS_SAFE_SELECTOR =
  'input, textarea, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], [contenteditable="false"], [contenteditable]';

const isCompatibleElement = (node: Element): node is HTMLElement => {
  if (!(node instanceof HTMLElement)) {
    return false;
  }

  if (node.hasAttribute('contenteditable')) {
    return true;
  }

  const tag = node.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea';
};

const applyExtensionGuards = (node: Element) => {
  if (!isCompatibleElement(node)) {
    return;
  }

  // CRITICAL: Don't apply autocomplete="off" to password manager fields
  // Password managers need these fields to have proper autocomplete attributes
  const autocompleteAttr = node.getAttribute('autocomplete');
  const isPasswordManagerField = 
    autocompleteAttr === 'email' ||
    autocompleteAttr === 'username' ||
    autocompleteAttr === 'email username' ||
    autocompleteAttr === 'current-password' ||
    autocompleteAttr === 'new-password' ||
    autocompleteAttr === 'one-time-code' ||
    (autocompleteAttr && autocompleteAttr.includes('email')) ||
    (autocompleteAttr && autocompleteAttr.includes('password')) ||
    (node instanceof HTMLInputElement && (
      node.type === 'password' ||
      node.type === 'email' ||
      (node.name && (node.name.toLowerCase().includes('password') || 
                     node.name.toLowerCase().includes('email') || 
                     node.name.toLowerCase().includes('username') ||
                     node.name.toLowerCase().includes('totp') ||
                     node.name.toLowerCase().includes('verificationcode')))
    ));

  Object.entries(EXTENSION_SAFE_ATTRS).forEach(([key, value]) => {
    // Skip autocomplete="off" for password manager fields
    if (key === 'autocomplete' && isPasswordManagerField) {
      return;
    }
    if (node.getAttribute(key) !== value) {
      node.setAttribute(key, value);
    }
  });
};

export const useExtensionCompatibility = (): void => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const applyToTree = (root: ParentNode | Element) => {
      if (root instanceof Element) {
        applyExtensionGuards(root);
      }

      const elements = root.querySelectorAll(FOCUS_SAFE_SELECTOR);
      elements.forEach(applyExtensionGuards);
    };

    applyToTree(document);

    const focusHandler = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Element) {
        // Check if this is a password manager field before applying guards
        const autocompleteAttr = target.getAttribute('autocomplete');
        const isPasswordManagerField = 
          autocompleteAttr === 'email' ||
          autocompleteAttr === 'username' ||
          autocompleteAttr === 'email username' ||
          autocompleteAttr === 'current-password' ||
          autocompleteAttr === 'new-password' ||
          autocompleteAttr === 'one-time-code' ||
          (autocompleteAttr && autocompleteAttr.includes('email')) ||
          (autocompleteAttr && autocompleteAttr.includes('password')) ||
          (target instanceof HTMLInputElement && (
            target.type === 'password' ||
            target.type === 'email' ||
            (target.name && (target.name.toLowerCase().includes('password') || 
                             target.name.toLowerCase().includes('email') || 
                             target.name.toLowerCase().includes('username') ||
                             target.name.toLowerCase().includes('totp') ||
                             target.name.toLowerCase().includes('verificationcode')))
          ));

        // Don't interfere with password manager fields
        if (!isPasswordManagerField) {
          applyExtensionGuards(target);

          if (target.getAttribute('data-extension-guard') === 'true') {
            // Prevent third-party content scripts from receiving this focus event
            // to avoid crashes like "Cannot read properties of undefined (reading 'control')"
            event.stopImmediatePropagation();
          }
        }
      }
    };

    document.addEventListener('focusin', focusHandler, true);

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof Element || node instanceof DocumentFragment) {
            applyToTree(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      document.removeEventListener('focusin', focusHandler, true);
      observer.disconnect();
    };
  }, []);
};

export default useExtensionCompatibility;

